import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import AdminShell from "../AdminShell";
import "./manager-login.css";

/**
 * RManagerLogin — /manager (Store Manager sign-in, orders-only portal).
 *
 * Visual/interaction ground truth: design-ref/admin/Manager Login.html
 * (ember identity — white card on a dark radial mine gradient).
 * Functional ground truth: frontend/src/pages/ManagerLoginPage.js:
 *   - POST /api/admin/login { email, password } (Username field sent as email)
 *   - FastAPI-style error handling (detail array → joined messages)
 *   - client gate: permissions must include "orders.read", else
 *     "This account does not have order management access."
 *   - stores adminToken + adminTokenExpires, sets managerMode="true"
 *   - redirects to /admin/orders
 *
 * Rendered via chromeless AdminShell (no sidebar/header on a login screen —
 * the full shell would fire the /api/admin/me auth guard and bounce
 * unauthenticated visitors away from the login page itself).
 */
const RManagerLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (Array.isArray(errorData.detail)) {
          const errorMsg = errorData.detail
            .map((err) => err.msg || err.message)
            .join(", ");
          throw new Error(errorMsg || "Validation failed");
        }
        throw new Error(errorData.detail || "Login failed");
      }

      const data = await response.json();
      const permissions = data.permissions || [];

      // This portal is for order management only
      if (!permissions.includes("orders.read")) {
        throw new Error("This account does not have order management access.");
      }

      // Store token and flag manager mode so the orders page shows the manager view
      localStorage.setItem("adminToken", data.access_token);
      localStorage.setItem("adminTokenExpires", Date.now() + data.expires_in * 1000);
      localStorage.setItem("managerMode", "true");

      navigate("/admin/orders");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminShell chromeless pageClass="pg-manager-login">
      <div className="ml-screen">
        <div className="ml-box">
          <div className="ml-brand">
            <span className="ml-brand-tile">
              <img
                src="/design/assets/umoona-emblem.png"
                alt="Umoona Opal Mine &amp; Museum"
              />
            </span>
            <div className="ml-brand-title">Order Management</div>
            <div className="ml-brand-eyebrow">UMOONA OPAL MINE &amp; MUSEUM</div>
          </div>

          <div className="ml-card">
            <div className="ml-card-head">
              <div className="ml-card-title">Store Manager Sign In</div>
              <span className="ml-badge">Orders Access Only</span>
            </div>

            <form onSubmit={handleLogin}>
              <label className="ml-field">
                <span className="ml-label">Username</span>
                <input
                  className="ml-input"
                  type="text"
                  placeholder="manager"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                />
              </label>

              <label className="ml-field ml-field--last">
                <span className="ml-label">Password</span>
                <div className="ml-pwwrap">
                  <input
                    className="ml-input ml-input--pw"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="ml-eye"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>

              {error && (
                <div className="ml-error" role="alert">
                  {error}
                </div>
              )}

              <button type="submit" className="ml-submit" disabled={loading}>
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <div className="ml-foot">
              <Link to="/admin/login">Admin login →</Link>
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
};

export default RManagerLogin;
